import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { map } from 'rxjs/operators';

import { AuthUser } from '@app/account/account-api.model';
import { UserMonthlyActivity } from '@app/learner-home/learner-home-api.model';

import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { MonthlyActivityService } from '@app/learner-home/services/monthly-activity.service';
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';

interface MonthlyActivityItem {
  icon: string;
  label: string;
  difference: number;
  monthlyCount: number;
}

@Component({
  selector: 'dgx-monthly-activity',
  templateUrl: './monthly-activity.component.html',
  styleUrls: ['./monthly-activity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthlyActivityComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public monthlyActivity: MonthlyActivityItem[] = [];
  public isLoading: boolean = true;
  public authUser: AuthUser;
  public i18n = this.translateService.instant([
    'DashboardNav_PositiveActivityChange',
    'DashboardNav_NegativeActivityChange',
    'DashboardNav_NoActivityChange',
    'DashboardNav_MonthlyActivity',
    'DashboardNav_ItemsViewed',
    'DashboardNav_Completions',
    'DashboardNav_PointsEarned',
    'DashboardNav_AllActivity',
  ]);

  constructor(
    private monthlyActivityService: MonthlyActivityService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {
    super();
  }

  get insightsUrl(): string {
    let url: string;
    const profileUrl = this.authUser.viewerProfile.profileUrl;
    url = `/profile/${profileUrl}/insights`;
    return url;
  }

  public ngOnInit(): void {
    this.authUser = this.authService.authUser;
    this.monthlyActivityService
      .getUserMonthlyActivity()
      .pipe(
        map((results) => this.parseUserMonthlyActivity(results)),
        this.takeUntilDestroyed()
      )
      .subscribe(
        (data) => {
          this.monthlyActivity = data;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        () => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      );
  }

  public getAriaLabelForMonthlyActivity(activity: MonthlyActivityItem): string {
    this.setPropsForActivityChangeStrings(activity);
    return activity.difference > 0
      ? this.i18n.DashboardNav_PositiveActivityChange
      : activity.difference < 0
      ? this.i18n.DashboardNav_NegativeActivityChange
      : this.i18n.DashboardNav_NoActivityChange;
  }

  public parseUserMonthlyActivity(
    activity: UserMonthlyActivity
  ): MonthlyActivityItem[] {
    const itemsViewed = {
      icon: 'eye-open',
      label: this.i18n.DashboardNav_ItemsViewed,
      difference: this.calculateDifference(
        activity.userViews,
        activity.userPastViews
      ),
      monthlyCount: activity.userViews,
    };

    const itemCompletions = {
      icon: 'checkmark',
      label: this.i18n.DashboardNav_Completions,
      difference: this.calculateDifference(
        activity.userCompletions,
        activity.userPastCompletions
      ),
      monthlyCount: activity.userCompletions,
    };

    const userPoints = {
      icon: 'accomplishment',
      label: this.i18n.DashboardNav_PointsEarned,
      difference: this.calculateDifference(
        activity.userPoints,
        activity.userPastPoints
      ),
      monthlyCount: Math.round(activity.userPoints),
    };

    return [itemsViewed, itemCompletions, userPoints];
  }

  public calculateDifference(current: number, past: number): number {
    return Math.round(current - past);
  }

  private setPropsForActivityChangeStrings(
    activity: MonthlyActivityItem
  ): void {
    const labelAndCountObject = {
      label: activity.label,
      count: activity.monthlyCount,
    };

    this.i18n.DashboardNav_PositiveActivityChange =
      this.translateService.instant(
        'DashboardNav_PositiveActivityChange',
        labelAndCountObject
      );
    this.i18n.DashboardNav_NegativeActivityChange =
      this.translateService.instant(
        'DashboardNav_NegativeActivityChange',
        labelAndCountObject
      );
    this.i18n.DashboardNav_NoActivityChange = this.translateService.instant(
      'DashboardNav_NoActivityChange',
      labelAndCountObject
    );
  }
}
